import type { BackgroundEffect } from '@/lib/videoEffects'

/**
 * Fundos prontos.
 *
 * Sao SVG embutido, nao foto: um gradiente de 300 bytes em data URL entra
 * instantaneamente, funciona offline e nao cobra mais um download de quem so
 * quer esconder a bagunca de casa. Foto de escritorio, alem de pesada, denuncia
 * cada falha do recorte — gradiente perdoa.
 *
 * Para uma foto de verdade, o caminho e o botao de enviar imagem: o arquivo
 * vira data URL no proprio navegador e nunca chega ao servidor.
 */
export interface BackgroundPreset {
    id: string
    label: string
    /** Amostra usada no seletor. */
    swatch: string
    effect: BackgroundEffect
}

function gradient(from: string, to: string, accent?: string): string {
    const glow = accent
        ? `<circle cx="620" cy="240" r="360" fill="${accent}" opacity="0.35"/>`
        : ''
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720">
        <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stop-color="${from}"/><stop offset="100%" stop-color="${to}"/>
        </linearGradient></defs>
        <rect width="1280" height="720" fill="url(#g)"/>${glow}
    </svg>`
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

const ESTUDIO = gradient('#12161c', '#05070a')
const VERDE = gradient('#0b1f18', '#05070a', '#19c37d')
const ROXO = gradient('#160f22', '#05070a', '#7c5cff')

export const BACKGROUND_PRESETS: BackgroundPreset[] = [
    {
        id: 'none',
        label: 'Sem efeito',
        swatch: 'linear-gradient(135deg, #2a2f37, #14171c)',
        effect: { kind: 'none' }
    },
    {
        id: 'blur',
        label: 'Desfocar',
        swatch: 'linear-gradient(135deg, #6b7280, #374151)',
        effect: { kind: 'blur' }
    },
    {
        id: 'estudio',
        label: 'Estudio',
        swatch: 'linear-gradient(135deg, #12161c, #05070a)',
        effect: { kind: 'image', src: ESTUDIO }
    },
    {
        id: 'verde',
        label: 'Verde MRI',
        swatch: 'linear-gradient(135deg, #0b1f18, #19c37d)',
        effect: { kind: 'image', src: VERDE }
    },
    {
        id: 'roxo',
        label: 'Roxo',
        swatch: 'linear-gradient(135deg, #160f22, #7c5cff)',
        effect: { kind: 'image', src: ROXO }
    }
]

/** Limite do upload: acima disso a data URL comeca a pesar na memoria da aba. */
export const MAX_BACKGROUND_BYTES = 8 * 1024 * 1024

/**
 * Le a imagem escolhida como data URL. Fica so nesta aba: nao sobe pro
 * servidor nem e enviada pros outros participantes — o que eles recebem e o
 * video ja composto.
 */
export function readBackgroundFile(file: File): Promise<BackgroundEffect> {
    return new Promise((resolve, reject) => {
        if (!file.type.startsWith('image/')) {
            reject(new Error('Escolha um arquivo de imagem.'))
            return
        }
        if (file.size > MAX_BACKGROUND_BYTES) {
            reject(new Error('Imagem muito grande (limite de 8 MB).'))
            return
        }

        const reader = new FileReader()
        reader.onload = () => resolve({ kind: 'image', src: String(reader.result) })
        reader.onerror = () => reject(new Error('Nao consegui ler a imagem.'))
        reader.readAsDataURL(file)
    })
}
